import { apiService } from './apiService';
import {
  DashboardOverviewResponse,
  DashboardConfiguration,
  DashboardWidget,
  ApiResponse,
  SystemStatus,
  PerformanceMetrics,
  EmergencyControls,
  AlertSlotMonitorRow,
  AlertSlotState,
} from '../types/dashboard';
import { formatRecentAlertMessage } from '../utils/alertUserFacingText';

export class DashboardService {
  private baseUrl = '/api/v1/dashboard';

  // Get dashboard overview data
  async getDashboardOverview(tenantId?: string, userId?: number, token?: string): Promise<ApiResponse<DashboardOverviewResponse>> {
    const headers: any = {};
    if (tenantId) headers['X-Tenant-ID'] = tenantId;
    if (userId) headers['X-User-ID'] = userId.toString();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    return apiService.get(`${this.baseUrl}/overview`, { headers });
  }

  // Get dashboard configuration
  async getDashboardConfig(tenantId?: string, userId?: number, token?: string): Promise<ApiResponse<DashboardConfiguration>> {
    const headers: Record<string, string> = {};
    if (tenantId) headers['X-Tenant-ID'] = tenantId;
    if (userId != null) headers['X-User-ID'] = userId.toString();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return apiService.get(`${this.baseUrl}/config`, { headers });
  }

  // Update dashboard configuration
  async updateDashboardConfiguration(
    tenantId: string, 
    userId: number, 
    config: Partial<DashboardConfiguration>
  ): Promise<ApiResponse<DashboardConfiguration>> {
    return apiService.put(`${this.baseUrl}/config`, config, {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId.toString()
      }
    });
  }

  // Get dashboard widgets
  async getDashboardWidgets(tenantId?: string, userId?: number, token?: string): Promise<ApiResponse<DashboardWidget[]>> {
    const headers: Record<string, string> = {};
    if (tenantId) headers['X-Tenant-ID'] = tenantId;
    if (userId != null) headers['X-User-ID'] = userId.toString();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    return apiService.get(`${this.baseUrl}/widgets`, { headers });
  }

  // Create dashboard widget
  async createDashboardWidget(
    tenantId: string, 
    userId: number, 
    widget: Omit<DashboardWidget, 'id' | 'tenantId' | 'userId' | 'createdAt' | 'updatedAt'>
  ): Promise<ApiResponse<DashboardWidget>> {
    return apiService.post(`${this.baseUrl}/widgets`, widget, {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId.toString()
      }
    });
  }

  // Update dashboard widget
  async updateDashboardWidget(
    tenantId: string, 
    userId: number, 
    widgetId: number, 
    widget: Partial<DashboardWidget>
  ): Promise<ApiResponse<DashboardWidget>> {
    return apiService.put(`${this.baseUrl}/widgets/${widgetId}`, widget, {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId.toString()
      }
    });
  }

  // Delete dashboard widget
  async deleteDashboardWidget(tenantId: string, userId: number, widgetId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`${this.baseUrl}/widgets/${widgetId}`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId.toString()
      }
    });
  }

  // Get user interface state
  async getUserInterfaceState(tenantId: string, userId: number): Promise<ApiResponse<any>> {
    return apiService.get(`${this.baseUrl}/ui-state`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId.toString()
      }
    });
  }

  // Update user interface state
  async updateUserInterfaceState(
    tenantId: string, 
    userId: number, 
    state: Record<string, any>
  ): Promise<ApiResponse<any>> {
    return apiService.put(`${this.baseUrl}/ui-state`, state, {
      headers: {
        'X-Tenant-ID': tenantId,
        'X-User-ID': userId.toString()
      }
    });
  }

  // Emergency control methods
  async triggerEmergencyStop(reason: string): Promise<ApiResponse<any>> {
    return apiService.post(`${this.baseUrl}/emergency/stop`, { reason }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async reactivateSystem(): Promise<ApiResponse<any>> {
    return apiService.post(`${this.baseUrl}/emergency/reactivate`, {}, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async updateEmergencySettings(settings: any): Promise<ApiResponse<any>> {
    return apiService.put(`${this.baseUrl}/emergency/settings`, settings, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // User profile methods
  async updateUserProfile(data: any): Promise<ApiResponse<any>> {
    return apiService.put(`${this.baseUrl}/profile`, data, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async changePassword(passwordData: any): Promise<ApiResponse<any>> {
    return apiService.put(`${this.baseUrl}/profile/password`, passwordData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // User settings methods
  async updateUserSettings(settings: any): Promise<ApiResponse<any>> {
    return apiService.put(`${this.baseUrl}/settings`, settings, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }
}

// --- Dashboard overview API returns { status, overview }; map to UI model (kept here so Docker builds always include it) ---

function mapSystemStatusRows(rows: unknown[]): SystemStatus {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      status: 'unknown',
      lastUpdate: new Date().toISOString(),
      uptime: 0,
      message: 'No status rows yet (run system checks or seed SystemStatus).',
    };
  }
  const rank: Record<string, number> = { error: 3, stopped: 3, unhealthy: 3, warning: 2, degraded: 2, healthy: 1, unknown: 0 };
  let best = 0;
  let overall: 'healthy' | 'warning' | 'error' | 'unknown' | 'stopped' = 'healthy';
  let lastIso = '';
  let uptimeSum = 0;
  let uptimeN = 0;
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const raw = String(r.status ?? '').toLowerCase();
    const mapped: keyof typeof rank =
      raw === 'unhealthy' || raw === 'error' ? 'error' :
      raw === 'degraded' || raw === 'warning' ? 'warning' :
      raw === 'healthy' ? 'healthy' :
      raw === 'stopped' ? 'stopped' : 'unknown';
    if ((rank[mapped] ?? 0) > best) {
      best = rank[mapped] ?? 0;
      overall =
        mapped === 'error' ? 'error' :
        mapped === 'warning' ? 'warning' :
        mapped === 'stopped' ? 'stopped' :
        mapped === 'healthy' ? 'healthy' : 'unknown';
    }
    const lc = r.last_check ?? r.lastCheck;
    if (typeof lc === 'string') {
      if (!lastIso || lc > lastIso) lastIso = lc;
    }
    const up = r.uptime_percentage ?? r.uptimePercentage;
    if (typeof up === 'number' && !Number.isNaN(up)) {
      uptimeSum += up;
      uptimeN += 1;
    }
  }
  return {
    status: overall === 'stopped' ? 'stopped' : overall,
    lastUpdate: lastIso || new Date().toISOString(),
    uptime: uptimeN ? Math.round(uptimeSum / uptimeN) : 0,
    message: rows.length ? `${rows.length} service(s) monitored` : undefined,
  };
}

function defaultPerformanceMetrics(): PerformanceMetrics {
  return {
    executionTime: { current: 0, average: 0, trend: 'stable' },
    successRate: { current: 0, average: 0, trend: 'stable' },
    totalAlerts: { today: 0, thisWeek: 0, thisMonth: 0 },
    systemHealth: { cpu: 0, memory: 0, database: 'unknown' },
    lowConfidenceCount: 0,
    failedExecutions: 0,
  };
}

function mapPerformanceMetrics(raw: unknown): PerformanceMetrics {
  const base = defaultPerformanceMetrics();
  if (!raw || typeof raw !== 'object') return base;
  const p = raw as Record<string, unknown>;
  const ex = p.executionTime as Record<string, unknown> | undefined;
  const sr = p.successRate as Record<string, unknown> | undefined;
  const ta = (p.totalAlerts ?? p.total_alerts) as Record<string, unknown> | undefined;
  const sh = p.systemHealth as Record<string, unknown> | undefined;
  return {
    executionTime: {
      current: Number(ex?.current ?? base.executionTime.current),
      average: Number(ex?.average ?? base.executionTime.average),
      trend: (ex?.trend as PerformanceMetrics['executionTime']['trend']) || 'stable',
    },
    successRate: {
      current: Number(sr?.current ?? base.successRate.current),
      average: Number(sr?.average ?? base.successRate.average),
      trend: (sr?.trend as PerformanceMetrics['successRate']['trend']) || 'stable',
    },
    totalAlerts: {
      today: Number(ta?.today ?? ta?.today_count ?? base.totalAlerts.today),
      thisWeek: Number(ta?.thisWeek ?? ta?.this_week ?? base.totalAlerts.thisWeek),
      thisMonth: Number(ta?.thisMonth ?? ta?.this_month ?? base.totalAlerts.thisMonth),
    },
    systemHealth: {
      cpu: Number(sh?.cpu ?? base.systemHealth.cpu),
      memory: Number(sh?.memory ?? base.systemHealth.memory),
      database: (sh?.database as PerformanceMetrics['systemHealth']['database']) || 'unknown',
    },
    lowConfidenceCount: Number(p.lowConfidenceCount ?? base.lowConfidenceCount),
    failedExecutions: Number(p.failedExecutions ?? base.failedExecutions),
  };
}

function defaultEmergency(): EmergencyControls {
  return { isStopped: false, canReactivate: true };
}

function mapEmergencyFromOverview(emergency: unknown): EmergencyControls {
  if (!Array.isArray(emergency) || emergency.length === 0) return defaultEmergency();
  const rows = emergency as Record<string, unknown>[];
  const stopRow = rows.find(
    (c) =>
      c.is_active === true &&
      String(c.control_type ?? c.controlType ?? '').toLowerCase().includes('stop'),
  );
  if (!stopRow) return defaultEmergency();
  return {
    isStopped: true,
    stoppedAt: stopRow.activated_at ? String(stopRow.activated_at) : undefined,
    stoppedBy: stopRow.activated_by ? String(stopRow.activated_by) : undefined,
    reason: stopRow.activation_reason ? String(stopRow.activation_reason) : undefined,
    canReactivate: true,
  };
}

/**
 * Map GET /api/v1/dashboard/overview JSON to EnhancedDashboard shape.
 * @param raw — response body
 * @param subscriptionInfo — from subscription API when available
 */
export function normalizeDashboardOverviewResponse(
  raw: unknown,
  subscriptionInfo: DashboardOverviewResponse['subscriptionInfo'] | null,
): DashboardOverviewResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  if (root.status === 'error') return null;
  const overview = (root.overview as Record<string, unknown>) || root;
  const systemRows = (overview.system_status ?? overview.systemStatus) as unknown[];
  const performanceRaw = overview.performance_metrics ?? overview.performanceMetrics;
  const emergencyRaw = overview.emergency_controls ?? overview.emergencyControls;
  const recentRaw = (overview.recent_alerts ?? overview.recentAlerts) as unknown[];
  const slotMonitorRaw = (overview.alert_slot_monitor ?? overview.alertSlotMonitor) as unknown[];

  const mapSlotMonitor = (rows: unknown[]): AlertSlotMonitorRow[] => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const r = row as Record<string, unknown>;
      const slotsRaw = (r.slots ?? []) as unknown[];
      const slots: AlertSlotState[] = slotsRaw.map((s) => {
        const v = String(s).toLowerCase();
        if (v === 'up' || v === 'down' || v === 'neutral' || v === 'active' || v === 'inactive') {
          return v as AlertSlotState;
        }
        return 'neutral';
      });
      const consensus = r.consensus as AlertSlotMonitorRow['consensus'];
      return {
        alertId: Number(r.alert_id ?? r.alertId ?? 0),
        stockSymbol: String(r.stock_symbol ?? r.stockSymbol ?? ''),
        positionType: String(r.position_type ?? r.positionType ?? ''),
        alertKind: String(r.alert_kind ?? r.alertKind ?? ''),
        triggerCount: Number(r.trigger_count ?? r.triggerCount ?? slots.length),
        slotSources: Array.isArray(r.slot_sources)
          ? (r.slot_sources as string[])
          : Array.isArray(r.slotSources)
            ? (r.slotSources as string[])
            : [],
        slotDescriptions: Array.isArray(r.slot_descriptions)
          ? (r.slot_descriptions as string[])
          : Array.isArray(r.slotDescriptions)
            ? (r.slotDescriptions as string[])
            : [],
        slots,
        consensus: consensus ?? null,
        lastTriggeredAt: (r.last_triggered_at ?? r.lastTriggeredAt) as string | null | undefined,
        lastSuccessAt: (r.last_success_at ?? r.lastSuccessAt) as string | null | undefined,
        lastAttemptAt: (r.last_attempt_at ?? r.lastAttemptAt) as string | null | undefined,
        lastAttemptStatus: (r.last_attempt_status ?? r.lastAttemptStatus) as string | null | undefined,
      };
    });
  };

  const mapRecentAlerts = (rows: unknown[]): DashboardOverviewResponse['recentAlerts'] => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const r = row as Record<string, unknown>;
      const st = String(r.status ?? '').toLowerCase();
      const status: 'success' | 'error' | 'pending' | 'low_confidence' =
        st === 'success' ? 'success' :
        st === 'error' ? 'error' :
        st === 'low_confidence' ? 'low_confidence' : 'pending';
      const ms = r.execution_time_ms ?? r.executionTimeMs;
      const execSec = typeof ms === 'number' && ms > 0 ? ms / 1000 : 0;
      const ts = r.timestamp ?? r.created_at ?? r.createdAt;
      const alertConfigIdRaw = r.alert_config_id ?? r.alertConfigId;
      const alertConfigId =
        typeof alertConfigIdRaw === 'number'
          ? alertConfigIdRaw
          : typeof alertConfigIdRaw === 'string' && /^\d+$/.test(alertConfigIdRaw)
            ? parseInt(alertConfigIdRaw, 10)
            : undefined;
      return {
        id: String(r.id ?? ''),
        stockSymbol: String(r.symbol ?? r.ticker ?? r.stockSymbol ?? ''),
        positionType: String(r.type ?? r.order_type ?? r.positionType ?? ''),
        status,
        tradeSide: String(r.trade_side ?? r.tradeSide ?? '').trim() || undefined,
        timestamp: ts instanceof Date ? ts.toISOString() : String(ts ?? ''),
        executionTime: execSec,
        confidenceScore: typeof r.confidence_score === 'number' ? Math.round(r.confidence_score) : 0,
        webhookUrl: String(r.webhook_url ?? r.webhookUrl ?? ''),
        secretWord: '',
        errorMessage:
          formatRecentAlertMessage(
            String(r.error_message ?? r.errorMessage ?? '').trim() || undefined,
            status
          ) ?? undefined,
        alertConfigId,
      };
    });
  };

  return {
    systemStatus: mapSystemStatusRows(Array.isArray(systemRows) ? systemRows : []),
    performanceMetrics: mapPerformanceMetrics(performanceRaw),
    recentAlerts: mapRecentAlerts(recentRaw),
    alertSlotMonitor: mapSlotMonitor(slotMonitorRaw),
    alertNotices: [],
    webhookSummary: { total: 0, active: 0, inactive: 0, error: 0 },
    subscriptionInfo: subscriptionInfo ?? {
      plan: 'trial',
      status: 'active',
      webhookLimit: 999,
      webhookUsage: 0,
      storageLimit: 1,
      storageUsage: 0,
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      autoRenew: false,
    },
    emergencyStatus: mapEmergencyFromOverview(emergencyRaw),
    lastUpdated: new Date().toISOString(),
  };
}

// Export singleton instance
export const dashboardService = new DashboardService();
export default dashboardService;
